import { request, requestWithMsg, MsgResult } from './request';

// 参-04 统一 Schema 管理:「字段组」型 Schema 的通用 API.
// 材料理化 (phys_schema) 与 设备 (device_schema) 结构同构 (groupKey/label/fields/sort/isEnabled),
// 用同一套类型 + 按 basePath 区分的 CRUD 工厂, 避免两份重复代码.

export interface SchemaGroupField {
    key: string;
    label: string;
    unit?: string;
    /** 值类型: 缺省数值; 'str'=自由文本 (如禁速带 JSON), 录入不做数字校验 */
    type?: string;
    /** 枚举选项: 非空时该字段的值只能从中选 (录入处渲染下拉) */
    options?: string[];
    /** 值来源字典类目 code (受控枚举, 如 source→light_source); 录入渲染字典下拉 */
    optionsSource?: string;
    /** 必填 (仅 SKU 规格模板使用, 2026-10-14): 选了该规格类型的 SKU 必须填此字段 */
    required?: boolean;
    /** 所属页签 / 分组 (2026-10-23, 组有 fieldLayout 时用; 见 utils/fieldGroups) */
    tab?: string;
    sec?: string;
}

export interface SchemaGroup {
    id: number;
    uuid: string;
    groupKey: string;
    label: string;
    src: string;
    /** 类别作用域 (仅材料理化组使用): material_category.categoryCode, 空 = 全部材料 */
    cat1?: string;
    /** @deprecated 旧单值二级细分, 已并入 cat2Keys (后端列暂留) */
    cat2?: string;
    /** 组内二级类别绑定 (仅材料理化组): 空 = 该类别全部材料; 非空 = 只有 type2 在清单内的材料显示/可写本组 */
    cat2Keys?: string[];
    /** 工艺框架作用域 (仅设备组使用): process_arch.code, 空 = 通用 */
    archCode?: string;
    /** 组内设备绑定 (仅设备组): 空 = 该框架全部设备; 非空 = 只有这些 deviceKey 显示/可写本组 */
    deviceKeys?: string[];
    /** 布局: flat=平铺(默认) / matrix=行(字典 rowSource)×列(fields) 矩阵 */
    layout?: string;
    /** 矩阵行来源 dict_entry.category (layout=matrix 时必填) */
    rowSource?: string;
    /** 矩阵行维度标题 (表头第一列), 如 "波长 (nm)" */
    rowLabel?: string;
    /** 组内 页签 → 分组 结构 JSON 串 (仅设备组, 2026-10-23); null = 平铺 */
    fieldLayout?: string | null;
    fields: string; // JSON 数组字符串 [{key,label,unit,options}]
    sort: number;
    isEnabled: boolean;
    /** 关联数 (列表接口聚合返回): 该模板作用域内在用的 设备台数/材料个数 (关联范围, 不论是否存值) */
    scopeCount?: number;
    /** 存值数 (列表接口聚合返回): 关联对象中存有非空值的 设备台数/材料个数, >0 时组不可删 */
    boundCount?: number;
    createdAt?: number;
    updatedAt?: number;
}

export interface GroupSchemaSavePayload {
    groupKey?: string;
    label?: string;
    src?: string;
    cat1?: string;
    /** 传 [] = 解绑 (回到类别全部材料); 不传 = 不改动 */
    cat2Keys?: string[];
    archCode?: string;
    /** 传 [] = 解绑 (回到框架全部设备); 不传 = 不改动 */
    deviceKeys?: string[];
    layout?: string;
    rowSource?: string;
    rowLabel?: string;
    /** 组内布局 JSON 串 (仅设备组): 不传 = 不改; 空串 = 取消分组 */
    fieldLayout?: string;
    fields?: string;
    sort?: number;
    isEnabled?: boolean;
}

/** 字段组 Schema 的 CRUD 客户端 (基于资源路径). base 例: 'phys-schemas' / 'device-schemas'.
 * Schema 显式绑定 (2026-08-09): 组按 (groupKey, 模板编码) 实例化 — 设备模板 = 工艺框架 archCode,
 * 材料模板 = 一级类别 cat1. update/remove 传 scopeCode 锁定单模板实例; 同名组多实例时后端强制要求. */
export interface GroupSchemaClient {
    list(): Promise<SchemaGroup[]>;
    create(
        p: GroupSchemaSavePayload & {
            groupKey: string;
            label: string;
            fields: string;
        },
    ): Promise<MsgResult<{ id?: number }>>;
    update(
        groupKey: string,
        p: GroupSchemaSavePayload,
        scopeCode?: string,
    ): Promise<MsgResult<null>>;
    remove(groupKey: string, scopeCode?: string): Promise<MsgResult<null>>;
    /** 组下仍存值的绑定对象清单 (设备 / 材料); 删除前查询, 有绑定时后端会拒绝删除 */
    bindings(groupKey: string, scopeCode?: string): Promise<SchemaBindings>;
    /** 模板作用域内的关联对象清单 (设备 / 材料); 按模板编码查, 组无关 */
    scope(scopeCode?: string): Promise<SchemaBindings>;
}

/** 绑定/关联清单项: name=展示名, code=业务键 (材料 xcode / 设备 deviceKey), 前端据 code 跳转详情. */
export interface BoundItem {
    name: string;
    code: string;
}

/** 字段组绑定/关联清单: total=对象数, items=清单项 (含展示名 + 业务键). */
export interface SchemaBindings {
    total: number;
    items: BoundItem[];
}

const BASE = '/v1';

// 后端把"定位用的作用域"统一叫 scope (材料侧实为 cat1、设备侧实为 archCode) —— 与
// "改成哪个作用域"的新值字段区分开, 所以这里不再按域拼不同的参数名.
export function makeGroupSchemaClient(base: string): GroupSchemaClient {
    return {
        list: () => request<SchemaGroup[]>(`${BASE}/${base}`),
        create: (p) =>
            requestWithMsg(`${BASE}/${base}`, { method: 'POST', body: p }),
        update: (groupKey, p, scopeCode) =>
            requestWithMsg(`${BASE}/${base}/update`, {
                method: 'POST',
                body: {
                    groupKey,
                    ...(scopeCode === undefined ? {} : { scope: scopeCode }),
                    ...p,
                },
            }),
        // 删除统一走 POST .../delete + 键在 body (2026-08-24: groupKey 可能含 "/",
        // 放路径会被拆段 404); scope 是作用域码 (设备=archCode, 材料=cat1).
        remove: (groupKey, scopeCode) =>
            requestWithMsg(`${BASE}/${base}/delete`, {
                method: 'POST',
                body: {
                    groupKey,
                    ...(scopeCode === undefined ? {} : { scope: scopeCode }),
                },
            }),
        bindings: (groupKey, scopeCode) =>
            request<SchemaBindings>(`${BASE}/${base}/bindings`, {
                method: 'POST',
                body: {
                    groupKey,
                    ...(scopeCode === undefined ? {} : { scope: scopeCode }),
                },
            }),
        scope: (scopeCode) =>
            request<SchemaBindings>(`${BASE}/${base}-scope`, {
                query:
                    scopeCode === undefined ? undefined : { scope: scopeCode },
            }),
    };
}

/** 材料理化字段组 (phys_schema): 模板编码 = 一级类别 cat1. */
export const physSchemaClient = makeGroupSchemaClient('phys-schemas');
/** 设备动态字段组 (device_schema): 模板编码 = 工艺框架 archCode. */
export const deviceSchemaClient = makeGroupSchemaClient('device-schemas');
/** SKU 规格模板 (sku_schema, 2026-10-14): 规格类型 + 字段 (含必填), 无作用域; SKU 自选类型, 值存 SKU 行上. */
export const skuSchemaClient = makeGroupSchemaClient('sku-schemas');

// --- 设备动态字段值 (device_ext_value): 按 (deviceKey, groupKey) 存 ---

export interface DeviceExtValue {
    id: number;
    deviceKey: string;
    groupKey: string;
    params: string; // JSON 对象字符串
}

export function listDeviceExtValues(
    deviceKey: string,
): Promise<DeviceExtValue[]> {
    return request<DeviceExtValue[]>(`${BASE}/devices/ext-values`, {
        method: 'POST',
        dedupe: true,
        body: { deviceKey },
    });
}

/** 整组覆盖某设备某字段组的值 (admin 即时, 普通用户走审批). */
export function upsertDeviceExtValue(
    deviceKey: string,
    groupKey: string,
    params: string,
): Promise<MsgResult<null>> {
    return requestWithMsg(`${BASE}/devices/ext-values/update`, {
        method: 'POST',
        body: { deviceKey, groupKey, params },
    });
}

/** 设备字段组是否适用于某设备: 组内设备绑定为空 = 该框架全部设备, 非空 = 只认清单内的设备.
 * 各设备侧入口 (工况页 / 能力卡片 / 新建弹窗 / 值编辑弹窗) 统一走这里, 与后端读写过滤同口径. */
export function groupAppliesToDevice(
    g: SchemaGroup,
    deviceKey: string,
): boolean {
    return !g.deviceKeys?.length || g.deviceKeys.includes(deviceKey);
}

/** 解析 fields JSON → 字段数组 (容错). */
export function parseGroupFields(json: string | undefined): SchemaGroupField[] {
    if (!json) return [];
    try {
        const arr = JSON.parse(json);
        return Array.isArray(arr) ? arr : [];
    } catch {
        return [];
    }
}
